import {
	Edit01,
	LayersTwo01,
	Map02,
	SwitchVertical01,
} from "@untitledui/icons";
import { useMemo } from "react";

import { Button } from "@/components/ui/buttons/button";
import { CloseButton } from "@/components/ui/buttons/close-button";
import { SearchInputWithClear } from "@/components/ui/input/search-input-with-clear";
import { Select } from "@/components/ui/select/select";
import { Tooltip } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import type { LocationManagementState } from "../../hooks/useLocationManagementState";
import type { ZoneBoundaryDrawConfig } from "../../map/draw/useMapboxDraw";

import {
	type RowActionItem,
	RowActionsMenu,
} from "../../components/row-actions-menu";
import { toTitleCase } from "../../utils/shared/strings";
import { LocationMapSidebar } from "./sidebar/location-map-sidebar";
import { LocationsSidebarList } from "./sidebar/locations-sidebar-list";

const ALL_LOCATIONS_SELECT_ID = "all-locations";

type LocationMapSidebarPaneProps = {
	/** Set when the map asks the sidebar to open a floor editor. */
	floorEditRequest?: null | { floorId: string; requestId: number };
	handleBackToAllLocations: () => void;
	handleSelectLocationFromList: (locationId: string) => void;
	/** Location switcher in the drilled-in sidebar header. */
	handleSelectLocationFromSwitcher: (locationId: string) => void;
	isReorderingFloors: boolean;
	isSidebarOpen: boolean;
	listSearchQuery: string;
	locationIdsWithInstallation: ReadonlySet<string>;
	locationInstallationCount: number;
	locationSelectItems: Array<{ id: string; label: string }>;
	mapState: LocationManagementState;
	mapViewportCenter: [number, number];
	onAddFloor: () => void;
	onAddInstallation: () => void;
	onAddSensor: (zoneId: string) => void;
	onAddZone: () => void;
	onCloseSidebar: () => void;
	onEditLocation: () => void;
	onFloorBoundaryDrawChange?: (
		config: undefined | ZoneBoundaryDrawConfig,
	) => void;
	setIsReorderingFloors: (next: boolean) => void;
	setListSearchQuery: (query: string) => void;
};

export const LocationMapSidebarPane = ({
	floorEditRequest = null,
	handleBackToAllLocations,
	handleSelectLocationFromList,
	handleSelectLocationFromSwitcher,
	isReorderingFloors,
	isSidebarOpen,
	listSearchQuery,
	locationIdsWithInstallation,
	locationInstallationCount,
	locationSelectItems,
	mapState,
	mapViewportCenter,
	onAddFloor,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onCloseSidebar,
	onEditLocation,
	onFloorBoundaryDrawChange,
	setIsReorderingFloors,
	setListSearchQuery,
}: LocationMapSidebarPaneProps) => {
	const {
		clearHoveredLocation,
		hoveredLocationId,
		hoverLocation,
		isEditing,
		isLocationSelected,
		location,
		locations,
	} = mapState;

	const locationMenuItems = useMemo((): RowActionItem[] => {
		if (isEditing || isReorderingFloors) return [];
		return [
			{
				icon: Edit01,
				id: "edit-location",
				label: "Edit location",
				onAction: onEditLocation,
			},
			{
				icon: LayersTwo01,
				id: "add-floor",
				label: "Add floor",
				onAction: onAddFloor,
			},
			{
				icon: Map02,
				id: "add-zone",
				label: "Add zone",
				onAction: onAddZone,
			},
		];
	}, [isEditing, isReorderingFloors, onAddFloor, onAddZone, onEditLocation]);

	return (
		<aside
			aria-hidden={!isSidebarOpen}
			aria-label={isLocationSelected ? "Zones and sensors" : "Locations"}
			className={cx(
				"absolute inset-y-0 right-0 z-40 flex min-h-0 w-[min(100%,28rem)] flex-col overflow-hidden rounded-r-lg border border-secondary bg-primary shadow-xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
				isSidebarOpen
					? "translate-x-0"
					: "pointer-events-none translate-x-full",
			)}
			inert={!isSidebarOpen ? true : undefined}
		>
			<div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
				{!isLocationSelected ? (
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<div className="my-4 shrink-0 px-4 md:px-6">
							<div className="flex items-center justify-between gap-2">
								<h3 className="text-md font-semibold text-primary">
									Locations
								</h3>
								<CloseButton
									label="Close sidebar"
									onClick={onCloseSidebar}
									size="xs"
								/>
							</div>
						</div>
						<LocationsSidebarList
							hoveredLocationId={hoveredLocationId}
							locationIdsWithInstallation={locationIdsWithInstallation}
							locations={locations}
							onHoverLocation={hoverLocation}
							onHoverLocationEnd={clearHoveredLocation}
							onSelectLocation={handleSelectLocationFromList}
							searchQuery={listSearchQuery}
							setSearchQuery={setListSearchQuery}
						/>
					</div>
				) : (
					<>
						<div className="my-4 shrink-0 px-4 md:px-6">
							<div className="flex items-center justify-between gap-2">
								<Select
									aria-label="Location"
									className="w-fit max-w-[calc(100%-5rem)] min-w-0"
									isDisabled={isEditing || isReorderingFloors}
									items={locationSelectItems}
									onChange={(value) => {
										if (!value) return;
										const nextId = String(value);
										if (nextId === ALL_LOCATIONS_SELECT_ID) {
											handleBackToAllLocations();
											return;
										}
										handleSelectLocationFromSwitcher(nextId);
									}}
									placeholder="Select a location"
									// Default popover is `w-(--trigger-width)`; short plain labels
									// would clip options — size to content, never below trigger.
									popoverClassName="w-auto max-w-xs min-w-(--trigger-width)"
									size="sm"
									value={location.id}
									variant="plain"
								>
									{(item) => (
										<Select.Item id={item.id} key={item.id}>
											{item.label}
										</Select.Item>
									)}
								</Select>
								<div className="flex shrink-0 items-center gap-1">
									{locationMenuItems.length > 0 ? (
										<RowActionsMenu
											ariaLabel={`Actions for ${toTitleCase(location.name)}`}
											items={locationMenuItems}
										/>
									) : null}
									<CloseButton
										label="Close sidebar"
										onClick={onCloseSidebar}
										size="xs"
									/>
								</div>
							</div>
							{locationInstallationCount === 0 && (
								<h3 className="mt-3 text-md font-semibold text-primary">
									Installation
								</h3>
							)}
							{locationInstallationCount === 0 ? (
								<p className="mt-1 text-sm text-tertiary">
									An installation is required before you can add zones and
									sensors.
								</p>
							) : !isEditing && !isReorderingFloors ? (
								<div className="mt-3 flex items-center gap-2">
									<SearchInputWithClear
										aria-label="Search floors, zones, or sensors"
										className="min-w-0 flex-1"
										onChange={setListSearchQuery}
										placeholder="Search floors, zones, or sensors..."
										size="sm"
										value={listSearchQuery}
									/>
									{(location.floors ?? []).length >= 2 ? (
										<Tooltip
											description="Drag floors to set zoom order. The top of the list is the first floor shown as you zoom in."
											placement="top"
											title="Reorder floors"
										>
											<Button
												aria-label="Reorder floors"
												color="secondary"
												iconLeading={SwitchVertical01}
												onClick={() => {
													setListSearchQuery("");
													setIsReorderingFloors(true);
												}}
												size="sm"
											/>
										</Tooltip>
									) : null}
								</div>
							) : null}
						</div>

						<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
							<LocationMapSidebar
								floorEditRequest={floorEditRequest}
								hasInstallation={locationInstallationCount > 0}
								isReorderingFloors={isReorderingFloors}
								isSidebarOpen={isSidebarOpen}
								mapState={mapState}
								mapViewportCenter={mapViewportCenter}
								onAddInstallation={onAddInstallation}
								onAddSensor={onAddSensor}
								onAddZone={onAddZone}
								onFloorBoundaryDrawChange={onFloorBoundaryDrawChange}
								onReorderModeChange={setIsReorderingFloors}
								searchQuery={
									isEditing || isReorderingFloors ? "" : listSearchQuery
								}
							/>
						</div>
					</>
				)}
			</div>
		</aside>
	);
};
